import '../translations'
import '../styles/tailwind.css'
import * as React from 'react'
import * as Sentry from '@sentry/react'
import { X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import i18n from '../translations'
import { SENTRY_DSN } from '../Constants'
import { Toaster } from '@/components/ui/sonner'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { PublicBooking, PublicEventType, SchedulingHost } from '../types'
import { observeHeight, postToParent } from './embed'
import Profile from './pages/Profile'
import BookerPage from './pages/BookerPage'
import BookingPage from './pages/BookingPage'
import NotFound from './pages/NotFound'
import PoweredBy from './components/PoweredBy'

Sentry.init({ dsn: SENTRY_DSN })

const SUPPORTED_LANGUAGES = ['en', 'nl', 'fr']

function browserLanguage() {
  const candidates = [...(navigator.languages || []), navigator.language]
  for (const candidate of candidates) {
    const code = (candidate || '').split('-')[0].toLowerCase()
    if (SUPPORTED_LANGUAGES.includes(code)) return code
  }
  return 'en'
}

// Set before first render so the booker never flashes the default language
i18n.changeLanguage(browserLanguage())

interface IProps {
  page: 'profile' | 'booker' | 'booking' | 'not_found'
  event_type?: PublicEventType
  event_types?: PublicEventType[]
  host?: SchedulingHost
  booking?: PublicBooking
  preview_token?: string | null
  embed: boolean
  closable: boolean
  theme?: 'light' | 'dark' | null
}

export default function Application(props: IProps) {
  const { t } = useTranslation()
  const ref = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    document.documentElement.lang = browserLanguage()
  }, [])

  React.useEffect(() => {
    if (props.theme === 'dark') document.documentElement.classList.add('dark')
  }, [props.theme])

  React.useEffect(() => (props.embed && ref.current ? observeHeight(ref.current) : undefined), [props.embed])

  let content: React.ReactNode
  switch (props.page) {
    case 'profile': content = <Profile host={props.host!} eventTypes={props.event_types || []} embed={props.embed} />; break
    case 'booker': content = <BookerPage eventType={props.event_type!} embed={props.embed} previewToken={props.preview_token} />; break
    case 'booking': content = <BookingPage booking={props.booking!} embed={props.embed} />; break
    default: content = <NotFound />
  }

  return (
    <React.Suspense fallback={null}>
      <div ref={ref} className={cn(
        'mx-auto w-full',
        props.embed ? 'p-0' : props.page === 'booker' ? 'flex min-h-svh flex-col justify-center px-4 py-6 sm:py-12' : 'max-w-5xl px-4 py-6 @container sm:py-12',
      )}>
        {props.embed && props.closable && (
          <div className='flex justify-end p-2'>
            <Button variant='ghost' size='icon' aria-label={t('Scheduling::Close')} onClick={() => postToParent({ type: 'bizzey:booking:close-request' })}>
              <X className='size-4' />
            </Button>
          </div>
        )}
        {content}
        <PoweredBy />
      </div>
      <Toaster />
    </React.Suspense>
  )
}
